import React, { useEffect } from "react";
import { useExcalidrawAPI } from "@excalidraw/excalidraw";

import { saveCurrentBoard } from "../lib/saveBoard";

/**
 * Global Cmd/Ctrl+S → "Save to Playbox".
 *
 * Mounted by PlbxBoardSync, which renders for the whole canvas lifetime — the
 * earlier handler lived in MainMenuPlbxItems, whose effect was dormant because
 * Radix unmounts MainMenu children whenever the hamburger menu is closed (so
 * the listener only attached while the menu was open). That is why Cmd+S never
 * produced a toast during normal drawing.
 *
 * Native Cmd+S (Excalidraw's actionSaveToActiveFile) is disabled via patch 04,
 * so file save/export stays only on Cmd/Ctrl+Shift+S and Cmd+S is free for the
 * cloud save here. We still:
 *   - match by e.code ("KeyS") first so non-Latin layouts (e.g. Russian, where
 *     the S key yields e.key="ы") work, with e.key as a fallback;
 *   - preventDefault to stop the browser "save page" default;
 *   - capture + stopImmediatePropagation as belt-and-suspenders.
 */
export const PlbxSaveHotkey: React.FC = () => {
  const excalidrawAPI = useExcalidrawAPI();

  useEffect(() => {
    // Read-only share viewer — no save.
    if (
      typeof window !== "undefined" &&
      (window.location.hostname.startsWith("share.") ||
        window.location.hash.startsWith("#board="))
    ) {
      return;
    }

    const handler = (e: KeyboardEvent) => {
      const isSaveKey =
        e.code === "KeyS" || (!!e.key && e.key.toLowerCase() === "s");
      if (
        (e.metaKey || e.ctrlKey) &&
        !e.shiftKey &&
        !e.altKey &&
        isSaveKey
      ) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        void saveCurrentBoard(excalidrawAPI);
      }
    };

    window.addEventListener("keydown", handler, { capture: true });
    return () =>
      window.removeEventListener("keydown", handler, {
        capture: true,
      } as any);
  }, [excalidrawAPI]);

  return null;
};

export default PlbxSaveHotkey;
